import React, { useState, useEffect } from 'react';
import { Table, Typography, Space, message, Button, Tag, Spin, Select, DatePicker, Input, Modal, Form } from 'antd';
import { ReloadOutlined, DownloadOutlined, ArrowLeftOutlined, MailOutlined, SaveOutlined } from '@ant-design/icons';
import { CSVLink } from 'react-csv';
import { useNavigate } from 'react-router';
import dayjs from 'dayjs';
import apiClient from '../../api.js';
import './AnalysedFile.css';
import { getEmailBody, statusOptions, getEmailSubject, riskLevels } from './common';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const AnalysedFile = ({ fileId }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fileInfo, setFileInfo] = useState(null);
  const [selectedRiskLevels, setSelectedRiskLevels] = useState([]);
  const [emailModalVisible, setEmailModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [emailForm] = Form.useForm();
  const [sendingEmail, setSendingEmail] = useState(false);
  const [userEmails, setUserEmails] = useState([]);
  const [loadingEmails, setLoadingEmails] = useState(false);
  const navigate = useNavigate();

  // Fetch user emails from API
  const fetchUserEmails = async () => {
    setLoadingEmails(true);
    try {
      const response = await apiClient.get('/email/users');
      if (response.data && response.data.users) {
        setUserEmails(response.data.users);
      }
    } catch (error) {
      console.error('Error fetching user emails:', error);
      message.error('Failed to fetch user emails');
      // Fallback to empty array
      setUserEmails([]);
    } finally {
      setLoadingEmails(false);
    }
  };

  // Handle email alert - open modal
  const handleSendAlert = (record) => {
    setSelectedRecord(record);
    
    const defaultSubject = getEmailSubject(record);
    const defaultBody = getEmailBody(record);
    emailForm.setFieldsValue({
      subject: defaultSubject,
      body: defaultBody
    });
    
    setEmailModalVisible(true);
  };

  const handleSendEmail = async (values) => {
    setSendingEmail(true);
    try {
      const response = await apiClient.post('/email/send-alert', {
        analysis_id: selectedRecord.id,
        subject: values.subject,
        body: values.body
      });

      if (response.status/100 === 2) {
        setEmailModalVisible(false);
        emailForm.resetFields();
        setSendingEmail(false);
        message.success('Alert email sent successfully!');
      } else {
        message.error('Failed to send alert email');
      }
    } catch (error) {
      console.error('Error sending alert email:', error);
      message.error(`Failed to send alert email: ${error.response?.data?.error || error.message}`);
    }
  };

  // Handle modal cancel
  const handleEmailModalCancel = () => {
    setEmailModalVisible(false);
    setSelectedRecord(null);
    emailForm.resetFields();
  };

  // Handle save
  const handleSave = async (record) => {
    try {
      // Prepare the data to send to backend
      const updateData = {
        assigned_to: record.assigned_to,
        deadline: record.deadline,
        status: record.status,
        comment: record.comment
      };

      const response = await apiClient.put(`/analysis/record/${record.id}`, updateData);

      if (response.status === 200) {
        message.success('Changes saved successfully!');
      } else {
        message.error('Failed to save changes');
      }
    } catch (error) {
      console.error('Error saving record:', error);
      message.error(`Failed to save changes: ${error.response?.data?.error || error.message}`);
    }
  };

  // Handle field changes
  const handleFieldChange = (index, field, value) => {
    const updatedData = [...data];
    updatedData[index] = {
      ...updatedData[index],
      [field]: value
    };
    setData(updatedData);
  };

  // Define table columns for the VSC file data
  const columns = [
    {
      title: 'Program',
      dataIndex: 'Program',
      key: 'Program',
      width: 80,
      ellipsis: true,
      sorter: (a, b) => (a.Program || '').localeCompare(b.Program || ''),
    },
    {
      title: 'Routine',
      dataIndex: 'Routine',
      key: 'Routine',
      width: 80,
      ellipsis: true,
      sorter: (a, b) => (a.Routine || '').localeCompare(b.Routine || ''),
    },
    {
      title: 'Rung',
      dataIndex: 'Rung',
      key: 'Rung',
      width: 60,
      ellipsis: true,
      sorter: (a, b) => (a.Rung || 0) - (b.Rung || 0),
    },
    {
      title: 'Code',
      dataIndex: 'Code',
      key: 'Code',
      width: 160,
      ellipsis: true,
      render: text => (
        <span 
          title={text}
          style={{ fontFamily: 'monospace', fontSize: '12px' }}
        >
          {text}
        </span>
      ),
    },
    {
      title: 'Anomaly Type',
      dataIndex: 'anomaly_type',
      key: 'anomaly_type',
      width: 110,
      ellipsis: true,
    },
    // {
    //   title: 'Explanation',
    //   dataIndex: 'explanation',
    //   key: 'explanation',
    //   ellipsis: true,
    //   render: text => <span title={text}>{text}</span>,
    // },
    // {
    //   title: 'Solution',
    //   dataIndex: 'solution',
    //   key: 'solution',
    //   ellipsis: true,
    //   render: text => <span title={text}>{text}</span>,
    // },
    {
      title: 'Alert',
      key: 'alert',
      width: 60,
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          icon={<MailOutlined />}
          onClick={() => handleSendAlert(record)}
          title="Send Email Alert"
        />
      ),
    },
    {
      title: 'Assign To',
      dataIndex: 'assignTo',
      key: 'assignTo',
      width: 120,
      ellipsis: true,
      render: (text, record, index) => (
        <Select
          value={record["assigned_to"] || undefined}
          onChange={(value) => handleFieldChange(index, 'assigned_to', value)}
          style={{ width: '100%' }}
          placeholder="Select"
          allowClear
          size="small"
          loading={loadingEmails}
        >
          {userEmails.map(user => (
            <Option key={user.email} value={user.email} title={user.email}>
              {user.name}
            </Option>
          ))}
        </Select>
      ),
    },
    {
      title: 'Due Date',
      dataIndex: 'dueDate',
      key: 'dueDate',
      width: 120,
      render: (text, record, index) => (
        <DatePicker
          value={record["deadline"]   ? dayjs(record["deadline"]) : null}
          onChange={(date) => handleFieldChange(index, 'deadline', date ? date.format('YYYY-MM-DD') : null)}
          style={{ width: '100%' }}
          placeholder="Select date"
          size="small"
          disabledDate={(current) => current && current < dayjs().startOf('day')}
          />
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (text, record, index) => (
        <Select
          value={record["status"]}
          onChange={(value) => handleFieldChange(index, 'status', value)}
          style={{ width: '100%' }}
          size="small"
        >
          {statusOptions.map(status => (
            <Option key={status} value={status}>{status}</Option>
          ))}
        </Select>
      ),
    },
    {
      title: 'Comments',
      dataIndex: 'comments',
      key: 'comments',
      width: 120,
      render: (text, record, index) => (
        <TextArea
          value={record["comment"] || ''}
          onChange={(e) => handleFieldChange(index, 'comment', e.target.value)}
          placeholder="Comments..."
          rows={1}
          style={{ width: '100%' }}
          size="small"
          />
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 60,
      render: (_, record, index) => (
        <Button
          type="primary"
          size="small"
          icon={<SaveOutlined />}
          onClick={() => handleSave(record, index)}
          title="Save Changes"
        />
      ),
    }
  ];

  const handleRiskLevelFilter = (riskLevel) => {
    setSelectedRiskLevels(prev => {
      if (prev.includes(riskLevel)) {
        // Remove if already selected
        return prev.filter(level => level !== riskLevel);
      } else {
        // Add if not selected
        return [...prev, riskLevel];
      }
    });
  };

  // Filter data based on selected risk levels
  const getFilteredData = () => {
    if (selectedRiskLevels.length === 0) {
      return data; // Show all if no filter selected
    }
    return data.filter(item => selectedRiskLevels.includes(item.risk_level));
  };

  // Fetch analysis data from API
  const fetchAnalysisData = async () => {
    if (!fileId) return;
    
    setLoading(true);
    try {
      const response = await apiClient.get(`/analysis/${fileId}/data`);
      if (response.data) {
        // Handle the new response format
        const { data: analysisData, filename, filepath, total_records } = response.data;
        setData(analysisData || []);
        
        // Set file info from the response
        setFileInfo({
          id: fileId,
          filename: filename || `File ${fileId}`,
          filepath: filepath || '',
          total_records: total_records || 0,
        });
      }

      if(response.status/100!==2){
        message.error(`Failed to fetch analysis data: ${response.data.error}`);
      }
    } catch (error) {
      console.error('Error fetching analysis data:', error);
      message.error('Failed to fetch analysis data');
      setData([]);
      setFileInfo({
        id: fileId,
        filename: `File ${fileId}`,
        filepath: '',
        total_records: 0,
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle back navigation
  const handleBack = () => {
    navigate('/analysis');
  };

  // Refresh data
  const handleRefresh = () => {
    fetchAnalysisData();
  };

  // Generate CSV data (use filtered data)
  const getCsvData = () => {
    const filteredData = getFilteredData();
    return filteredData.map(item => ({
      Program: item.Program || '',
      Routine: item.Routine || '',
      Rung: item.Rung || '',
      Code: item.Code.replaceAll('"', "'") || '',
      'Anomaly Type': item.anomaly_type || '',
      'Risk Level': item.risk_level || '',
      Explanation: item.explanation || '',
      Solution: item.solution || '',
      'Assign To': item.assigned_to || '',
      'Due Date': item.deadline || '',
      Status: item.status || 'Pending',
      Comments: item.comment || '',
    }));
  };

  // Get summary statistics (use filtered data)
  const getSummaryStats = () => {
    const filteredData = getFilteredData();
    const total = filteredData.length;
    const riskCounts = filteredData.reduce((acc, item) => {
      const risk = item.risk_level || 'Unknown';
      acc[risk] = (acc[risk] || 0) + 1;
      return acc;
    }, {});

    return { total, riskCounts };
  };

  // Initialize data on component mount
  useEffect(() => {
    fetchUserEmails();
    fetchAnalysisData();
  }, [fileId]);

  if (loading && data.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem' }}>
        <Spin size="large" />
        <p style={{ marginTop: '1rem' }}>Loading analysis data...</p>
      </div>
    );
  }

  const summaryStats = getSummaryStats();
  const filteredData = getFilteredData();

  return (
    <div className="analysed-file-container">
      <div className="analysed-file-header">
        <div className="analysed-header-top">
          <div className="analysed-header-left">
            <div>
              <Title level={3} style={{ margin: 0 }}>
                {fileInfo?.filename || `Analysis Results - File ${fileId}`}
              </Title>
              {fileInfo?.filepath && (
                <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
                  {fileInfo.filepath}
                </Typography.Text>
              )}
              </div>
          </div>
          <div className="analysed-header-right">
            <Space>
              <CSVLink
                data={getCsvData()}
                filename={`analysis-${fileId}-${new Date().toISOString().split('T')[0]}.csv`}
                className="csv-download-link"
              >
                <Button icon={<DownloadOutlined />}>Export CSV</Button>
              </CSVLink>
              <Button
                icon={<ReloadOutlined />}
                onClick={handleRefresh}
                loading={loading}
              >
                Refresh
              </Button>
            </Space>
          </div>
        </div>

        {/* Combined Risk Level Filter and Summary Stats */}
        <div className="risk-level-filters">
          <div className="filter-section">
            <Text strong style={{ marginRight: '1rem' }}>Filter by Risk Level:</Text>
            <Space>
              {riskLevels.map(({ key, label, color }) => (
                <Button
                  key={key}
                  type={selectedRiskLevels.includes(key) ? 'primary' : 'default'}
                  danger={color === 'danger' && selectedRiskLevels.includes(key)}
                  style={{
                    backgroundColor: 
                      selectedRiskLevels.includes(key) 
                        ? color === 'danger' 
                          ? '#ff4d4f' 
                          : color === 'warning' 
                          ? '#faad14' 
                          : '#52c41a'
                        : undefined,
                    borderColor:
                      selectedRiskLevels.includes(key)
                        ? color === 'danger' 
                          ? '#ff4d4f' 
                          : color === 'warning' 
                          ? '#faad14' 
                          : '#52c41a'
                        : undefined
                  }}
                  onClick={() => handleRiskLevelFilter(key)}
                >
                  {label}: {data.filter(item => item.risk_level === key).length || 0}
                </Button>
              ))}
              {selectedRiskLevels.length > 0 && (
                <Button 
                  type="link" 
                  onClick={() => setSelectedRiskLevels([])}
                  style={{ padding: '0 8px' }}
                >
                  Clear All
                </Button>
              )}
            </Space>
          </div>
        </div>
      </div>

      <Table
        className="analysed-file-table"
        columns={columns}
        dataSource={filteredData}
        loading={loading}
        pagination={{
          defaultPageSize: 20,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} items`,
          pageSizeOptions: ['10', '20', '50', '100'],
        }}
        scroll={{ x: false }}
        rowKey={(record, index) => `${record.Program}-${record.Routine}-${record.Rung}-${index}`}
        size="middle"
        expandable={{
          expandedRowRender: record => (
            <div style={{ margin: 0 }}>
              <p><strong>Explanation:</strong> {record.explanation}</p>
              <p><strong>Solution:</strong> {record.solution}</p>
            </div>
          ),
          rowExpandable: record => record.explanation || record.solution,
        }}
      />

      {/* Email Alert Modal */}
      <Modal
        title="Send Email Alert"
        open={emailModalVisible}
        onCancel={handleEmailModalCancel}
        width={700}
        footer={[
          <Button key="cancel" onClick={handleEmailModalCancel}>
            Cancel
          </Button>,
          <Button 
            key="send" 
            type="primary" 
            icon={<MailOutlined />}
            onClick={() => emailForm.submit()}
            disabled={sendingEmail}
          >
            {sendingEmail ? 'Email Sent' : 'Send Alert'}
          </Button>,
        ]}
      >
        {selectedRecord && (
          <div>
            <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '6px' }}>
              <Text strong>Recipients: </Text>
              <Text>All configured email recipients will receive this alert</Text>
              <br />
              <Text strong>Analysis Record: </Text>
              <Text>{selectedRecord.Program} - {selectedRecord.Routine} - Rung {selectedRecord.Rung}</Text>
            </div>
            
            <Form
              form={emailForm}
              layout="vertical"
              onFinish={handleSendEmail}
            >
              <Form.Item
                label="Subject"
                name="subject"
                rules={[{ required: true, message: 'Please enter email subject' }]}
              >
                <Input placeholder="Enter email subject" />
              </Form.Item>
              
              <Form.Item
                label="Message Body"
                name="body"
                rules={[{ required: true, message: 'Please enter email body' }]}
              >
                <TextArea
                  rows={12}
                  placeholder="Enter email body"
                  style={{ fontFamily: 'monospace', fontSize: '13px' }}
                />
              </Form.Item>
            </Form>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default AnalysedFile; 